import { useState } from 'react';
import { DateInput, FORM_LABEL_CLASS, FORM_TEXTAREA_CLASS, Modal } from '@safetyhub/ui';
import { exemptionBasesForObligation, type LegalObligationExemptionBasisValue } from '@safetyhub/shared';
import { EditModalFooter } from '../common/EditModalFooter';
import { todayDateInputInTimezone } from '../../lib/localDate';
import { fulfilledAtToIso } from './EvidenceModal';

/** 서버 DTO의 메모 상한(`LEGAL_OBLIGATION_EVIDENCE_NOTE_MAX`)과 같은 값 — 화면 쪽 안전선 */
const NOTE_MAX_LENGTH = 500;

export interface ExemptionSubmitInput {
  basis: LegalObligationExemptionBasisValue;
  file: File;
  fulfilledAt: string;
  note?: string;
}

export interface ExemptionModalProps {
  open: boolean;
  /** 의무 카탈로그 키 — 선택지(근거 조항)의 정본은 shared `exemptionBasesForObligation` */
  obligationKey: string;
  obligationTitle: string;
  /** 대상자 줄 라벨(이름 (직위)) */
  subjectLabel: string;
  /** 현장 유효 시간대 — 날짜 → UTC 변환과 「오늘」(EvidenceModal과 같은 규약) */
  timezone: string;
  submitting: boolean;
  errorMessage: string | null;
  onSubmit: (input: ExemptionSubmitInput) => void;
  onClose: () => void;
}

/**
 * **사람 단위 교육 면제** 등록(2026-11-04 · 사용자 결정 「사람 줄마다 면제(근거 조항 + 증빙)」) — 근거 조항(시행규칙 제30조)과 증빙 파일(필수)을
 * 받는다. 면제는 그 사람의 그 기간을 이행처럼 닫는다(서버 `attachExemption`). 선택지는 그 의무의 것만이고(신규교육 ↔ 보수교육) 서버가 같은 대조를 한다.
 */
export default function ExemptionModal({
  open,
  obligationKey,
  obligationTitle,
  subjectLabel,
  timezone,
  submitting,
  errorMessage,
  onSubmit,
  onClose,
}: ExemptionModalProps) {
  const bases = exemptionBasesForObligation(obligationKey);
  const [basis, setBasis] = useState<LegalObligationExemptionBasisValue | null>(bases.length === 1 ? bases[0].basis : null);
  const [file, setFile] = useState<File | null>(null);
  const today = todayDateInputInTimezone(timezone);
  const [fulfilledAt, setFulfilledAt] = useState(today);
  const [note, setNote] = useState('');
  const trimmedNote = note.trim();
  const isDirty = (bases.length > 1 && basis !== null) || file !== null || trimmedNote !== '' || fulfilledAt !== today;

  const blockedReason = !basis
    ? '면제 근거를 고르세요.'
    : !file
      ? '증빙 파일을 선택하세요.'
      : !fulfilledAt
        ? '면제가 성립한 날을 선택하세요.'
        : '';

  const handleSubmit = () => {
    if (blockedReason || submitting || !basis || !file) return;
    onSubmit({ basis, file, fulfilledAt: fulfilledAtToIso(fulfilledAt, timezone), ...(trimmedNote ? { note: trimmedNote } : {}) });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="교육 면제 등록"
      size="md"
      closeLocked={submitting}
      isDirty={isDirty}
      footer={
        <EditModalFooter
          isDirty={isDirty}
          blockedReason={blockedReason}
          submitLabel="면제 등록"
          submitDisabled={!!blockedReason || submitting}
          busy={submitting}
          busyLabel="등록 중..."
          onSubmit={handleSubmit}
          onFallbackClose={onClose}
        />
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-700">
          <span className="font-bold text-ink-900">{subjectLabel}</span>의 <span className="font-bold text-ink-900">{obligationTitle}</span>을
          면제로 기록합니다. 근거 조항과 증빙(확인서·이수증·자격 증빙)을 함께 남기며, 기록하면 이 사람의 그 기간이 닫힙니다.
        </p>

        <fieldset>
          <legend className={FORM_LABEL_CLASS}>면제 근거 (산업안전보건법 시행규칙 제30조)</legend>
          <div className="space-y-2">
            {bases.map((b) => {
              const checked = basis === b.basis;
              return (
                <label
                  key={b.basis}
                  className={`flex cursor-pointer items-start gap-3 rounded-[10px] border px-3 py-2.5 ${
                    checked ? 'border-brand-500 bg-brand-50' : 'border-line hover:bg-surface-hover'
                  }`}
                >
                  <input
                    type="radio"
                    name="legal-exemption-basis"
                    value={b.basis}
                    checked={checked}
                    onChange={() => setBasis(b.basis)}
                    disabled={submitting}
                    className="mt-1 accent-brand-600"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-bold text-ink-900">{b.label}</span>
                    <span className="block text-xs text-ink-500">{b.description}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        <div>
          <label htmlFor="legal-exemption-file" className={FORM_LABEL_CLASS}>
            증빙 파일 (필수 · PDF·이미지·오피스 문서, 10MB 이하)
          </label>
          <input
            id="legal-exemption-file"
            type="file"
            accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={submitting}
            className="block w-full text-sm text-ink-700"
          />
        </div>

        <div>
          <label htmlFor="legal-exemption-date" className={FORM_LABEL_CLASS}>
            면제가 성립한 날
          </label>
          <DateInput
            id="legal-exemption-date"
            label="면제가 성립한 날"
            value={fulfilledAt}
            onChange={setFulfilledAt}
            max={today}
            disabled={submitting}
            className="w-full"
          />
          <p className="mt-1 text-xs text-ink-500">
            보수교육 면제(제30조②·③)는 대신한 교육을 이수한 날을 고르세요 — 그날부터 다음 보수교육 기한이 정해집니다.
          </p>
        </div>

        <div>
          <label htmlFor="legal-exemption-note" className={FORM_LABEL_CLASS}>
            메모 (선택)
          </label>
          <textarea
            id="legal-exemption-note"
            className={FORM_TEXTAREA_CLASS}
            value={note}
            maxLength={NOTE_MAX_LENGTH}
            onChange={(e) => setNote(e.target.value)}
            disabled={submitting}
            placeholder="예: 1998년 지정기관 교육·시험 합격(자격증 사본 첨부)"
          />
        </div>

        {errorMessage && (
          <p role="alert" className="rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {errorMessage}
          </p>
        )}
      </div>
    </Modal>
  );
}
